import { isAxiosError } from 'axios'

import type { ServiceQueryOptionType } from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { RecapExtClassResponse, ResponseDataType } from './types'

const RECAP_REQUEST_TIMEOUT_MS = 30_000
const RECAP_QUERY_RETRY = 3

const RECAP_EXT_CLASS_BPP_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['recap_ext_class_bpp', key] as const

const recapGet = <T>(url: string) =>
  api.get<T>(url, { timeout: RECAP_REQUEST_TIMEOUT_MS })

const emptyRecap = (): RecapExtClassResponse<ResponseDataType> => ({
  data: { detail: [] },
  message: 'No data found'
})

const getNationalRecapExtClassBpp = (): ServiceQueryOptionType<
  RecapExtClassResponse<ResponseDataType>
> => ({
  retry: RECAP_QUERY_RETRY,
  queryKey: RECAP_EXT_CLASS_BPP_QUERY_KEY({ type: 'national' }),
  queryFn: async (): Promise<RecapExtClassResponse<ResponseDataType>> => {
    try {
      const res = await recapGet<RecapExtClassResponse<ResponseDataType>>(
        '/report/kelembagaan/bpp/rekap'
      )

      return {
        message: res.data.message,
        data: res.data.data,
        meta: res.data.meta
      }
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return emptyRecap()
      }

      throw handleAxiosError(error)
    }
  }
})

const getProvinceRecapExtClassBpp = (
  provinceCode?: string
): ServiceQueryOptionType<RecapExtClassResponse<ResponseDataType>> => ({
  retry: RECAP_QUERY_RETRY,
  queryKey: RECAP_EXT_CLASS_BPP_QUERY_KEY({
    type: 'province',
    province_code: provinceCode
  }),
  queryFn: async (): Promise<RecapExtClassResponse<ResponseDataType>> => {
    try {
      const res = await recapGet<RecapExtClassResponse<ResponseDataType>>(
        `/report/kelembagaan/bpp/rekap/province/${provinceCode}`
      )

      return {
        message: res.data.message,
        data: res.data.data,
        meta: res.data.meta
      }
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return emptyRecap()
      }

      throw handleAxiosError(error)
    }
  }
})

const getCityRecapExtClassBpp = ({
  provinceCode,
  cityCode
}: {
  provinceCode?: string
  cityCode?: string
}): ServiceQueryOptionType<RecapExtClassResponse<ResponseDataType>> => ({
  retry: RECAP_QUERY_RETRY,
  queryKey: RECAP_EXT_CLASS_BPP_QUERY_KEY({
    type: 'city',
    province_code: provinceCode,
    city_code: cityCode
  }),
  queryFn: async (): Promise<RecapExtClassResponse<ResponseDataType>> => {
    try {
      const res = await recapGet<RecapExtClassResponse<ResponseDataType>>(
        `/report/kelembagaan/bpp/rekap/province/${provinceCode}/city/${cityCode}`
      )

      return {
        message: res.data.message,
        data: res.data.data,
        meta: res.data.meta
      }
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return emptyRecap()
      }

      throw handleAxiosError(error)
    }
  }
})

export const recapExtClass = {
  queryKey: RECAP_EXT_CLASS_BPP_QUERY_KEY,
  national: getNationalRecapExtClassBpp,
  province: getProvinceRecapExtClassBpp,
  city: getCityRecapExtClassBpp
}
